import { useState } from 'react';
import { User, Loader2, Check, AlertCircle } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { api, ApiError } from '@/lib/api';

type UserRole = 'super_admin' | 'admin' | 'manager' | 'user';

/**
 * Get initials from a user's name
 * Examples: "John Doe" -> "JD", "Alice" -> "A", "" -> "?"
 */
function getInitials(name: string | undefined): string {
  if (!name || name.trim() === '') {
    return '?';
  }

  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Get role badge styling based on user role
 */
function getRoleBadgeClass(role: UserRole | undefined): string {
  const baseClass = 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium';

  if (role === 'super_admin' || role === 'admin') {
    return `${baseClass} bg-purple-100 text-purple-700`;
  }

  if (role === 'manager') {
    return `${baseClass} bg-green-100 text-green-700`;
  }

  return `${baseClass} bg-blue-100 text-blue-700`;
}

/**
 * Format role label for display
 */
function formatRoleLabel(role: UserRole | undefined): string {
  if (role === 'super_admin') {
    return 'Super Admin';
  }
  if (role === 'admin') {
    return 'Admin';
  }
  if (role === 'manager') {
    return 'Manager';
  }
  return 'User';
}

interface FormData {
  name: string;
  email: string;
  currentPassword: string;
  newPassword: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  currentPassword?: string;
  newPassword?: string;
  general?: string;
}

type SaveStatus = 'idle' | 'saving' | 'success' | 'error';

/**
 * ProfileCard Component
 *
 * Displays and allows editing of the current user's profile information.
 * Matches the approved UI design in docs/views/settings.html lines 220-291.
 */
export function ProfileCard() {
  const user = useAuthStore(state => state.user);
  const updateUser = useAuthStore(state => state.updateUser);

  // Initialize form with user data - using user.id as key to reset form when user changes
  const [formData, setFormData] = useState<FormData>(() => ({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
  }));
  const [errors, setErrors] = useState<FormErrors>({});
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.newPassword && formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (formData.newPassword && !formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required to change password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaveStatus('saving');
    setErrors({});

    try {
      // Build update payload - only include changed fields
      const updatePayload: {
        name?: string;
        email?: string;
        currentPassword?: string;
        newPassword?: string;
      } = {};

      if (formData.name !== user?.name) {
        updatePayload.name = formData.name;
      }

      if (formData.email !== user?.email) {
        updatePayload.email = formData.email;
      }

      if (formData.newPassword) {
        updatePayload.currentPassword = formData.currentPassword;
        updatePayload.newPassword = formData.newPassword;
      }

      // Only make API call if there are changes
      if (Object.keys(updatePayload).length === 0) {
        setSaveStatus('success');
        setTimeout(() => setSaveStatus('idle'), 2000);
        return;
      }

      const updatedUser = await api.updateProfile(updatePayload);

      // Update auth store with new user data
      updateUser({
        name: updatedUser.name,
        email: updatedUser.email,
      });

      // Clear password fields after successful update
      setFormData(prev => ({ ...prev, currentPassword: '', newPassword: '' }));

      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      setSaveStatus('error');

      if (error instanceof ApiError) {
        if (error.code === 'EMAIL_EXISTS') {
          setErrors({ email: 'This email is already in use' });
        } else if (error.code === 'INVALID_PASSWORD') {
          setErrors({ general: 'Current password is incorrect' });
        } else {
          setErrors({ general: error.message });
        }
      } else {
        setErrors({ general: 'An unexpected error occurred' });
      }

      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  // Check if form has changes
  const hasChanges =
    formData.name !== (user?.name || '') ||
    formData.email !== (user?.email || '') ||
    formData.currentPassword !== '' ||
    formData.newPassword !== '';

  // Get button content based on save status
  const getButtonContent = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        );
      case 'success':
        return (
          <>
            <Check className="w-4 h-4" />
            Saved!
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle className="w-4 h-4" />
            Failed
          </>
        );
      default:
        return 'Save Changes';
    }
  };

  // Get button class based on save status
  const getButtonClass = () => {
    const baseClass =
      'w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2';

    switch (saveStatus) {
      case 'saving':
        return `${baseClass} text-muted-foreground bg-muted cursor-not-allowed`;
      case 'success':
        return `${baseClass} text-green-600 bg-green-100`;
      case 'error':
        return `${baseClass} text-red-600 bg-red-100`;
      default:
        if (!hasChanges) {
          return `${baseClass} text-primary-foreground bg-primary/50 cursor-not-allowed`;
        }
        return `${baseClass} text-primary-foreground bg-primary hover:bg-primary/90`;
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Card Header */}
      <div className="p-4 sm:p-5 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-foreground">Profile</h2>
            <p className="text-sm text-muted-foreground">Your personal information</p>
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4 sm:p-5">
        {/* Profile Display */}
        <div className="flex items-center gap-3 sm:gap-4 mb-6">
          {/* Avatar */}
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <span className="text-lg sm:text-xl font-semibold text-primary">
              {getInitials(formData.name || user?.name)}
            </span>
          </div>

          {/* User Info */}
          <div className="min-w-0 flex-1">
            <p className="font-medium text-foreground truncate">
              {formData.name || user?.name || 'Unknown User'}
            </p>
            <p className="text-sm text-muted-foreground truncate">
              {formData.email || user?.email || 'No email'}
            </p>
            <span className={`${getRoleBadgeClass(user?.role)} mt-1`}>
              {formatRoleLabel(user?.role)}
            </span>
          </div>
        </div>

        {/* General Error */}
        {errors.general && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
            {errors.general}
          </div>
        )}

        {/* Form Fields */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="profile-name"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Full Name
              </label>
              <input
                id="profile-name"
                type="text"
                value={formData.name}
                onChange={e => handleChange('name', e.target.value)}
                className={`w-full h-10 px-3 rounded-lg border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.name ? 'border-red-500 bg-red-50' : 'border-input bg-background'
                }`}
                placeholder="Enter your full name"
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            <div>
              <label
                htmlFor="profile-email"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Email Address
              </label>
              <input
                id="profile-email"
                type="email"
                value={formData.email}
                onChange={e => handleChange('email', e.target.value)}
                className={`w-full h-10 px-3 rounded-lg border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.email ? 'border-red-500 bg-red-50' : 'border-input bg-background'
                }`}
                placeholder="Enter your email"
              />
              {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
            </div>

            <div>
              <label
                htmlFor="profile-current-password"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                Current Password
              </label>
              <input
                id="profile-current-password"
                type="password"
                value={formData.currentPassword}
                onChange={e => handleChange('currentPassword', e.target.value)}
                className={`w-full h-10 px-3 rounded-lg border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.currentPassword ? 'border-red-500 bg-red-50' : 'border-input bg-background'
                }`}
                placeholder="Enter current password"
              />
              {errors.currentPassword ? (
                <p className="mt-1 text-xs text-red-500">{errors.currentPassword}</p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  Required only when changing password
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="profile-password"
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                New Password
              </label>
              <input
                id="profile-password"
                type="password"
                value={formData.newPassword}
                onChange={e => handleChange('newPassword', e.target.value)}
                className={`w-full h-10 px-3 rounded-lg border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.newPassword ? 'border-red-500 bg-red-50' : 'border-input bg-background'
                }`}
                placeholder="Enter new password"
              />
              {errors.newPassword ? (
                <p className="mt-1 text-xs text-red-500">{errors.newPassword}</p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  Leave blank to keep current password
                </p>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-6 flex justify-end">
            <button
              type="submit"
              disabled={saveStatus === 'saving' || !hasChanges}
              className={getButtonClass()}
            >
              {getButtonContent()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
